import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { api } from "../lib/api";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import {
  BookOpen, Download, Map, Users, GitBranch, Gauge, Landmark, Globe, GitMerge,
  FolderKanban, Award, FileText, RefreshCw, Wallet, Coins, ShieldCheck, HelpCircle,
  Quote, ArrowRight, Lightbulb, CheckCircle2,
} from "lucide-react";

const SECTION_ICONS = {
  overview: Map, readiness: Gauge, catalog: Landmark, sources: Globe, matched: GitMerge,
  cases: FolderKanban, "grants-discover": Award, "grants-apply": FileText,
  "grants-refresh": RefreshCw, "finance-offset": Wallet, "icp-offset": Coins,
  roles: ShieldCheck, faq: HelpCircle,
};

export default function GreenFinancingGuide() {
  const [g, setG] = useState(null);
  const [busy, setBusy] = useState(false);
  const [active, setActive] = useState("journey");
  const location = useLocation();

  useEffect(() => { api.get("/gf-guide").then((r) => setG(r.data)).catch(() => {}); }, []);

  useEffect(() => {
    if (!g) return;
    const hash = location.hash?.replace("#", "");
    if (hash) setTimeout(() => document.getElementById(hash)?.scrollIntoView({ behavior: "smooth" }), 150);
  }, [g, location.hash]);

  const go = (id) => { setActive(id); document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" }); };

  const downloadPdf = async () => {
    setBusy(true);
    try {
      const r = await api.get("/gf-guide/export.pdf", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const el = document.createElement("a");
      el.href = url; el.download = "green_financing_user_guide.pdf"; el.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Could not generate the PDF"); } finally { setBusy(false); }
  };

  if (!g) return <div className="p-8 text-muted-foreground text-sm">Loading guide…</div>;

  const toc = [
    ["journey", "The 5-stage journey", Map],
    ["personas", "Who uses this module", Users],
    ["lifecycles", "Status lifecycles", GitBranch],
    ...g.sections.map((s) => [s.id, s.title.split(" — ")[0], SECTION_ICONS[s.id] || BookOpen]),
  ];

  return (
    <div className="p-6 lg:p-8" data-testid="gf-guide-page">
      <div className="mb-8 flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="label-eyebrow mb-1">Learn · Green Financing Module</div>
          <h1 className="font-display text-4xl font-bold tracking-tighter flex items-center gap-3">
            <BookOpen className="h-8 w-8 text-accent" /> {g.title}
          </h1>
          <p className="text-muted-foreground text-sm mt-1 max-w-3xl">
            End-to-end process flows for every feature — bank financing, grants, matching, AI assistance and the
            financial view of carbon — with detailed steps and realistic worked examples.
          </p>
        </div>
        <Button variant="primary" onClick={downloadPdf} disabled={busy} data-testid="gf-guide-download-pdf">
          <Download className="h-4 w-4" /> {busy ? "Preparing…" : "Download PDF"}
        </Button>
      </div>

      <div className="rounded-sm border border-amber-500/30 bg-amber-500/5 p-3 text-[11px] text-amber-500/90 mb-8 max-w-4xl" data-testid="gf-guide-mock-notice">
        {g.mock_notice}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[240px_1fr] gap-8">
        <nav className="hidden lg:block" data-testid="gf-guide-toc">
          <div className="sticky top-6 space-y-1 max-h-[calc(100vh-3rem)] overflow-y-auto pr-1">
            {toc.map(([id, label, Icon]) => (
              <button key={id} onClick={() => go(id)} data-testid={`gf-toc-${id}`}
                className={`w-full text-left flex items-center gap-2 px-3 py-2 rounded-sm text-sm transition-colors ${active === id ? "bg-accent/10 text-accent" : "text-muted-foreground hover:bg-surface-hover"}`}>
                <Icon className="h-4 w-4 shrink-0" /> <span className="truncate">{label}</span>
              </button>
            ))}
          </div>
        </nav>

        <div className="space-y-12 max-w-4xl">
          <section id="journey" className="scroll-mt-6" data-testid="gf-guide-journey">
            <SectionTitle icon={Map}>The 5-stage journey</SectionTitle>
            <div className="space-y-3">
              {g.journey.map((j) => (
                <div key={j.stage} className="card-flat p-4 flex items-start gap-4">
                  <div className="shrink-0 min-w-[110px]">
                    <div className="font-display font-bold text-accent">{j.stage}</div>
                    <div className="text-[10px] text-muted-foreground uppercase tracking-wider mt-0.5">{j.where}</div>
                  </div>
                  <p className="text-sm text-muted-foreground leading-relaxed">{j.what}</p>
                </div>
              ))}
            </div>
          </section>

          <section id="personas" className="scroll-mt-6" data-testid="gf-guide-personas">
            <SectionTitle icon={Users}>Who uses this module</SectionTitle>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {g.personas.map((p) => (
                <div key={p.role} className="card-flat p-4">
                  <div className="font-display font-semibold text-sm">{p.role}</div>
                  <p className="text-xs text-muted-foreground mt-1 leading-relaxed">{p.focus}</p>
                </div>
              ))}
            </div>
          </section>

          <section id="lifecycles" className="scroll-mt-6" data-testid="gf-guide-lifecycles">
            <SectionTitle icon={GitBranch}>Status lifecycles at a glance</SectionTitle>
            <div className="space-y-4">
              {g.lifecycles.map((lc) => (
                <div key={lc.name} className="card-flat p-4">
                  <div className="font-display font-semibold text-sm mb-2">{lc.name}</div>
                  <div className="flex flex-wrap items-center gap-1.5">
                    {lc.flow.map((s, i) => (
                      <React.Fragment key={s}>
                        <Badge variant="outline" className="text-[10px] font-mono">{s}</Badge>
                        {i < lc.flow.length - 1 && <ArrowRight className="h-3 w-3 text-muted-foreground shrink-0" />}
                      </React.Fragment>
                    ))}
                  </div>
                  <p className="text-[11px] text-muted-foreground mt-2 italic">{lc.alt}</p>
                </div>
              ))}
            </div>
          </section>

          {g.sections.map((sec) => {
            const Icon = SECTION_ICONS[sec.id] || BookOpen;
            return (
              <section key={sec.id} id={sec.id} className="scroll-mt-6" data-testid={`gf-guide-section-${sec.id}`}>
                <SectionTitle icon={Icon}>{sec.title}</SectionTitle>
                <div className="flex flex-wrap gap-2 mb-3 text-[11px]">
                  <Badge variant="accent" className="text-[10px]">{sec.where}</Badge>
                  <Badge variant="outline" className="text-[10px]">{sec.roles}</Badge>
                </div>
                <p className="text-sm text-muted-foreground leading-relaxed mb-4">{sec.purpose}</p>

                <div className="space-y-3 mb-4">
                  {sec.steps.map((st, i) => (
                    <div key={i} className="flex items-start gap-3" data-testid={`gf-step-${sec.id}-${i}`}>
                      <div className="h-6 w-6 rounded-sm bg-accent/10 border border-accent/20 flex items-center justify-center shrink-0 font-mono text-[11px] text-accent mt-0.5">{i + 1}</div>
                      <div>
                        <div className="text-sm font-medium">{st.action}</div>
                        <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{st.detail}</p>
                      </div>
                    </div>
                  ))}
                </div>

                {sec.example && (
                  <div className="rounded-sm border border-amber-500/25 bg-amber-500/[0.04] p-4 mb-4" data-testid={`gf-example-${sec.id}`}>
                    <div className="flex items-center gap-2 mb-2 text-amber-500/90">
                      <Quote className="h-3.5 w-3.5" />
                      <span className="text-xs font-semibold">{sec.example.title}</span>
                    </div>
                    <div className="space-y-1.5">
                      {sec.example.rows.map(([k, v]) => (
                        <div key={k} className="grid grid-cols-1 sm:grid-cols-[140px_1fr] gap-x-3 text-xs">
                          <span className="text-muted-foreground">{k}</span>
                          <span>{v}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {sec.outputs?.length > 0 && (
                  <div className="flex items-start gap-2 text-xs text-[#00E65B]/90 mb-2">
                    <CheckCircle2 className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                    <span><span className="font-semibold">Outputs: </span>{sec.outputs.join(" · ")}</span>
                  </div>
                )}
                {(sec.tips || []).map((t, i) => (
                  <div key={i} className="flex items-start gap-2 text-[11px] text-muted-foreground mb-1">
                    <Lightbulb className="h-3.5 w-3.5 shrink-0 mt-0.5 text-amber-400" />
                    <span>{t}</span>
                  </div>
                ))}
              </section>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function SectionTitle({ icon: Icon, children }) {
  return (
    <h2 className="font-display text-2xl font-bold tracking-tight mb-3 flex items-center gap-2 border-b border-border pb-2">
      <Icon className="h-5 w-5 text-accent shrink-0" /> {children}
    </h2>
  );
}
